import { useTranslation } from "react-i18next"

import { ArrowLeft, Maximize2, Minimize2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { DownloadButton } from "@/features/file-viewer/components/download-button"

type CitationSidebarHeaderProps = {
  title: string
  /** Public revision sequence from the exact cited source, never a provider token. */
  sourceRevision?: string
  /** Blob URL for download (unmodified when available, else indexed). */
  downloadUrl: string
  /** Filename hint for the available download artifact. */
  downloadFileName?: string
  /** Identifies an extracted-text fallback so the action is labeled honestly. */
  downloadKind?: "extracted" | "original"
  isFocused: boolean
  isFocusable: boolean
  closeable: boolean
  onToggleFocus: () => void
  onClose: () => void
  onBack?: () => void
  backLabel?: string
}

export function CitationSidebarHeader({
  title,
  sourceRevision,
  downloadUrl,
  downloadFileName,
  downloadKind,
  isFocused,
  isFocusable,
  closeable,
  onToggleFocus,
  onClose,
  onBack,
  backLabel,
}: CitationSidebarHeaderProps) {
  const { t } = useTranslation()
  const resolvedBackLabel =
    backLabel ?? t("canvas.back_to_document", { defaultValue: "Back to document" })

  let downloadTooltip: string | undefined
  if (downloadKind === "original") {
    downloadTooltip = t("analysispanel.fileviewer.download_original_file", {
      defaultValue: "Download original file",
    })
  } else if (downloadKind === "extracted") {
    downloadTooltip = t("analysispanel.fileviewer.download_extracted_text", {
      defaultValue: "Download extracted text",
    })
  }

  return (
    <div className="flex shrink-0 flex-col border-b">
      {onBack && (
        <div className="border-b px-1 py-1">
          <Button
            aria-label={resolvedBackLabel}
            className="h-8 gap-1.5 px-2"
            size="sm"
            variant="ghost"
            onClick={onBack}
          >
            <ArrowLeft className="size-4" />
            <span className="text-sm">{resolvedBackLabel}</span>
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between p-2">
        <div className="mr-2 min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold">{title}</h3>
          {sourceRevision !== undefined && (
            <p className="text-muted-foreground text-xs">
              {t("analysispanel.fileviewer.cited_revision", { defaultValue: "Cited revision" })}{" "}
              {sourceRevision}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <DownloadButton
            fileUrl={downloadUrl}
            title={downloadFileName ?? title}
            tooltip={downloadTooltip}
          />
          {isFocusable && (
            <Button
              className="h-8 w-8 shrink-0 p-0"
              size="icon"
              variant="ghost"
              aria-label={
                isFocused
                  ? t("analysispanel.exit_focus", { defaultValue: "Exit focus mode" })
                  : t("analysispanel.focus", { defaultValue: "Focus mode" })
              }
              onClick={onToggleFocus}
            >
              {isFocused ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
          )}
          {closeable && (
            <Button
              aria-label={t("analysispanel.close", { defaultValue: "Close" })}
              className="h-8 w-8 shrink-0 p-0"
              size="icon"
              variant="ghost"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
